/**
 * Everything this build was told, and the only file in the app that names a build-time global.
 *
 * The app reads no environment variable (ADR-073). What it has instead is a small set of constants
 * that `web/buildDefines.ts` substitutes as literals at compile time, and confining them to one
 * module buys two things a scattering of `__FEATIT_…__` reads would not:
 *
 * - **One answer to "what varies between builds?"** — this file, in full. A reader who wants to
 *   know what a desktop build differs from a hosted one by does not have to grep for a convention.
 * - **A seam tests can move.** A `define` is textual substitution, so `vi.stubGlobal` cannot reach
 *   one: by the time a test runs, the identifier is gone and a literal stands where it was. A test
 *   that needs a different build — `deploymentChoice.test.ts` needs one with an address baked in —
 *   mocks this module instead, which is a seam that exists rather than one wished for.
 */

/** Which build this is, compared against the deployment's to notice that a release has landed. */
export const BUILD_ID: string = __FEATIT_BUILD_ID__;

/**
 * The client contract this bundle speaks (T-0329, `web/client-contract.json`). A deployment whose
 * `build-info.json` names a higher one no longer serves this bundle, and the page reloads.
 */
export const CLIENT_CONTRACT: number = __FEATIT_CLIENT_CONTRACT__;

/**
 * The deployment this build points at, or `''` when it points at wherever it was served from.
 *
 * Non-empty only in a desktop build. See `features/onboarding/deploymentAddress.ts` for what is
 * done with it, and `scripts/desktop-build.mjs` for where it comes from.
 */
export const BUILT_IN_DEPLOYMENT_ADDRESS: string = __FEATIT_DEPLOYMENT_ADDRESS__;

/**
 * How often an idle tab asks whether a deploy window is open, as a string, or `''` for the default.
 *
 * Left unparsed on purpose: `readMaintenancePollIntervalMs` is what decides whether a value is
 * usable, and having one parser rather than two is what keeps a malformed number from meaning one
 * thing here and another there.
 */
export const MAINTENANCE_POLL_INTERVAL_MS: string = __FEATIT_MAINTENANCE_POLL_INTERVAL_MS__;

/**
 * Whether a development server runs the deployment reload monitor, as a production build always
 * does (I-0892). Set only by the lane's e2e server, so the update notice's specs run there too.
 */
export const DEPLOYMENT_RELOAD_IN_DEV: boolean = __FEATIT_DEPLOYMENT_RELOAD_IN_DEV__;

/**
 * The commit an ungated dev preview serves, or `''` for every other build (F-0309 DP-4). Set only by
 * `scripts/dev-preview.sh`, which serves dev from a commit the gate has not passed yet;
 * `UngatedPreviewBar` then says so.
 */
export const UNGATED_PREVIEW_SHA: string = __FEATIT_PREVIEW_SHA__;

/**
 * Whether this build offers the pseudo-locale and a locale override (T-0471, IN-3, IN-7): true only for an
 * end-to-end build or a development server, never for the shipped app.
 */
export const I18N_TEST_LOCALES: boolean = __FEATIT_I18N_TEST_LOCALES__;
